// src/components/activities/NivelesConcienciaActivity.jsx

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { LightBulbIcon, ArrowLeftCircleIcon, ArrowRightCircleIcon, ArrowRightIcon, ChevronDownIcon, ListBulletIcon, XMarkIcon } from '@heroicons/react/24/solid';

// Datos de la Escala de Conciencia, con el nivel "Iluminación" añadido al final.
const levelsData = [
  { level: "Vergüenza", score: 20, category: 'Fuerza', emotion: "Humillación, Sentirse sin valor", paradigm: "¿Cómo puedo esconderme? No merezco existir.", color: "from-gray-800 to-gray-900", textColor: "text-gray-300" },
  { level: "Culpa", score: 30, category: 'Fuerza', emotion: "Remordimiento, Culpar", paradigm: "Es mi culpa. Soy una mala persona.", color: "from-red-900 to-red-950", textColor: "text-red-200" },
  { level: "Apatía", score: 50, category: 'Fuerza', emotion: "Desesperanza, Desgano, Procrastinación", paradigm: "¿Para qué intentarlo? Nada tiene sentido.", color: "from-slate-800 to-slate-900", textColor: "text-slate-300" },
  { level: "Melancolía", score: 60, category: 'Fuerza', emotion: "Tristeza profunda, Depresión", paradigm: "Todo está perdido. Nunca volveré a ser feliz.", color: "from-blue-900 to-blue-950", textColor: "text-blue-200" },
  { level: "Nostalgia", score: 75, category: 'Fuerza', emotion: "Pesar, Añoranza del pasado", paradigm: "Ojalá las cosas fueran como antes. Extraño lo que perdí.", color: "from-cyan-900 to-cyan-950", textColor: "text-cyan-200" },
  { level: "Miedo", score: 100, category: 'Fuerza', emotion: "Ansiedad, Inseguridad", paradigm: "El mundo es un lugar peligroso. Algo malo va a pasar.", color: "from-orange-900 to-orange-950", textColor: "text-orange-200" },
  { level: "Deseo", score: 125, category: 'Fuerza', emotion: "Anhelo, Celos, Adicción", paradigm: "Necesito tener eso para ser feliz. Mi vida está incompleta sin ello.", color: "from-amber-800 to-amber-900", textColor: "text-amber-200" },
  { level: "Ira", score: 150, category: 'Fuerza', emotion: "Frustración, Resentimiento, Estrés", paradigm: "¡Esto es injusto! Si no fuera por ellos, yo estaría bien.", color: "from-rose-800 to-rose-900", textColor: "text-rose-200" },
  { level: "Orgullo", score: 175, category: 'Fuerza', emotion: "Soberbia, Exigencia, Rigidez", paradigm: "Yo tengo la razón y ellos están equivocados. Mi camino es el único.", color: "from-indigo-800 to-indigo-900", textColor: "text-indigo-200" },
  { level: "Coraje", score: 200, category: 'Poder', emotion: "Afirmación, Determinación", paradigm: "Puedo superar esto. Tengo la capacidad de cambiar.", color: "from-yellow-700 to-yellow-800", textColor: "text-yellow-100" },
  { level: "Neutralidad", score: 250, category: 'Poder', emotion: "Confianza, Desapego, Calma", paradigm: "Las cosas son como son. Fluyo con la vida sin resistencia.", color: "from-lime-800 to-lime-900", textColor: "text-lime-200" },
  { level: "Voluntad", score: 310, category: 'Poder', emotion: "Optimismo, Inspiración", paradigm: "Estoy aquí para contribuir. Mi éxito apoya el éxito de otros.", color: "from-green-700 to-green-800", textColor: "text-green-200" },
  { level: "Aceptación", score: 350, category: 'Poder', emotion: "Perdón, Comprensión", paradigm: "Entiendo mi papel en mi propia vida. Acepto la realidad tal como es.", color: "from-teal-700 to-teal-800", textColor: "text-teal-200" },
  { level: "Razón", score: 400, category: 'Poder', emotion: "Entendimiento, Sabiduría", paradigm: "Busco la verdad objetiva para tomar mejores decisiones.", color: "from-cyan-700 to-cyan-800", textColor: "text-cyan-200" },
  { level: "Amor", score: 500, category: 'Poder', emotion: "Reverencia, Gratitud, Amor incondicional", paradigm: "Veo la belleza y la conexión en todo. Todo es perfecto.", color: "from-purple-700 to-purple-800", textColor: "text-purple-200" },
  { level: "Alegría", score: 540, category: 'Poder', emotion: "Serenidad, Felicidad interior", paradigm: "La existencia misma es una fuente de felicidad. Todo está bien.", color: "from-pink-700 to-pink-800", textColor: "text-pink-200" },
  { level: "Paz", score: 600, category: 'Poder', emotion: "Dicha, Iluminación", paradigm: "Soy uno con el universo. El ser trasciende la percepción.", color: "from-sky-600 to-sky-700", textColor: "text-sky-100" },
  { level: "Iluminación", score: 700, category: 'Poder', emotion: "Conciencia Pura, Inefable", paradigm: "El Ser es completo en sí mismo. La conciencia es la única realidad.", color: "from-fuchsia-500 to-violet-400", textColor: "text-fuchsia-100" },
];

const NivelesConcienciaActivity = ({ nextActivity, onProceed }) => {
    // MODIFICACIÓN: Inicia el estado con un índice aleatorio.
    const [currentIndex, setCurrentIndex] = useState(() => Math.floor(Math.random() * levelsData.length));
    const [isModalOpen, setIsModalOpen] = useState(false);
    const currentLevel = levelsData[currentIndex];

    const handleNext = () => {
        if (currentIndex < levelsData.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    const TheorySection = () => (
        <div className="bg-gray-900 p-6 rounded-xl border border-orange-500/20 mb-8">
            <h2 className="text-2xl font-bold text-orange-400 mb-4 text-center">La Pirámide de la Creación</h2>
            <div className="flex flex-col items-center gap-2">
                <div className="text-center">
                    <h3 className="text-xl font-bold text-purple-300">Creencias</h3>
                    <p className="text-sm font-semibold text-purple-400/80 mb-1">(Inconsciente)</p>
                    <p className="text-gray-400 max-w-sm">Las "verdades" fundamentales que aceptaste sobre la vida, el dinero, el amor y sobre ti mismo. Gobiernan todo lo demás.</p>
                </div>
                <ChevronDownIcon className="w-8 h-8 text-white/30 my-1" />
                <div className="text-center">
                    <h3 className="text-xl font-bold text-yellow-300">Emociones</h3>
                    <p className="text-sm font-semibold text-yellow-400/80 mb-1">(Subconsciente)</p>
                    <p className="text-gray-400 max-w-sm">Son energía en movimiento, la vibración que emites constantemente, generada por tus creencias.</p>
                </div>
                <ChevronDownIcon className="w-8 h-8 text-white/30 my-1" />
                <div className="text-center">
                    <h3 className="text-xl font-bold text-cyan-300">Pensamientos y Acciones</h3>
                    <p className="text-sm font-semibold text-cyan-400/80 mb-1">(Consciente)</p>
                    <p className="text-gray-400 max-w-sm">Lo que piensas y haces cada día. Son la consecuencia directa de tus emociones y creencias.</p>
                </div>
            </div>
            <p className="mt-6 text-orange-200/90 italic text-center">
                Para un cambio permanente, debemos ir a la raíz: las creencias. Esta escala te muestra el mapa.
            </p>
        </div>
    );

    const FullTableModal = () => (
        <div 
            className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4 animate-fade-in"
            onClick={() => setIsModalOpen(false)}
        >
            <div 
                className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex justify-between items-center p-4 border-b border-gray-700 flex-shrink-0">
                    <h2 className="text-xl font-bold text-white">Tabla de Niveles de Conciencia</h2>
                    <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-white">
                        <XMarkIcon className="w-8 h-8"/>
                    </button>
                </div>
                <div className="overflow-y-auto p-1">
                    <table className="w-full text-left">
                        <thead className="sticky top-0 bg-gray-900">
                            <tr className="border-b border-gray-600">
                                <th className="p-3 text-sm font-semibold text-gray-300">Nivel</th>
                                <th className="p-3 text-sm font-semibold text-gray-300 text-center">Score</th>
                                <th className="p-3 text-sm font-semibold text-gray-300">Categoría</th>
                                <th className="p-3 text-sm font-semibold text-gray-300 hidden md:table-cell">Emoción Dominante</th>
                            </tr>
                        </thead>
                        <tbody>
                            {levelsData.map((level, index) => (
                                <tr key={index} className="border-b border-gray-800 hover:bg-gray-800/50">
                                    <td className={`p-3 font-bold ${level.textColor}`}>{level.level}</td>
                                    <td className={`p-3 font-mono text-center ${level.textColor}`}>{level.score}</td>
                                    <td className="p-3">
                                        <span className={`px-2 py-1 text-sm font-bold rounded-full ${level.category === 'Poder' ? 'bg-green-400/10 text-green-300' : 'bg-red-400/10 text-red-300'}`}>
                                            {level.category}
                                        </span>
                                    </td>
                                    <td className="p-3 text-gray-400 hidden md:table-cell">{level.emotion}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    return (
        <div className="max-w-3xl mx-auto animate-fade-in">
            {isModalOpen && <FullTableModal />}
            
            <TheorySection />

            <div className='text-center mb-8'>
                <button 
                    onClick={() => setIsModalOpen(true)}
                    className="inline-flex items-center gap-2 bg-gray-800 text-gray-300 font-semibold py-2 px-4 rounded-lg hover:bg-gray-700 hover:text-white transition-colors"
                >
                    <ListBulletIcon className="w-5 h-5"/>
                    Ver Tabla Completa
                </button>
            </div>

            <div className={`relative p-6 rounded-xl bg-gradient-to-br ${currentLevel.color} border border-white/10 transition-all duration-500`}>
                <div className="flex justify-between items-start mb-4">
                    <h3 className={`text-3xl font-extrabold ${currentLevel.textColor}`}>{currentLevel.level}</h3>
                    <span className={`px-4 py-1 rounded-full font-bold text-lg ${currentLevel.category === 'Poder' ? 'bg-green-400/20 text-green-300' : 'bg-red-400/20 text-red-300'}`}>
                        {currentLevel.category}
                    </span>
                    <span className={`text-4xl font-thin opacity-50 ${currentLevel.textColor}`}>{currentLevel.score}</span>
                </div>

                <div className="space-y-4 my-6 text-lg">
                    <div className="bg-black/20 p-4 rounded-lg">
                        <p className="text-sm font-bold text-gray-400">EMOCIÓN DOMINANTE</p>
                        <p className={`${currentLevel.textColor} font-semibold`}>{currentLevel.emotion}</p>
                    </div>
                     <div className="bg-black/20 p-4 rounded-lg">
                        <p className="text-sm font-bold text-gray-400">VISIÓN DEL MUNDO (PARADIGMA)</p>
                        <p className={`italic ${currentLevel.textColor}`}>"{currentLevel.paradigm}"</p>
                    </div>
                </div>

                <div className="flex justify-between items-center mt-8">
                    <button onClick={handlePrev} disabled={currentIndex === 0} className="disabled:opacity-30 transition">
                        <ArrowLeftCircleIcon className="w-12 h-12 text-white/50 hover:text-white"/>
                    </button>
                    <span className="font-bold text-white/80">{currentIndex + 1} / {levelsData.length}</span>
                    <button onClick={handleNext} disabled={currentIndex === levelsData.length - 1} className="disabled:opacity-30 transition">
                        <ArrowRightCircleIcon className="w-12 h-12 text-white/50 hover:text-white"/>
                    </button>
                </div>
            </div>

            <div className="w-full bg-gray-700 rounded-full h-2.5 mt-4">
                <div className="bg-orange-500 h-2.5 rounded-full" style={{ width: `${((currentIndex + 1) / levelsData.length) * 100}%` }}></div>
            </div>

            <div className="mt-12 text-center">
                <div className="bg-gray-900 p-6 rounded-xl border border-green-500/20 mb-8 max-w-2xl mx-auto">
                     <LightBulbIcon className="w-10 h-10 text-green-400 mx-auto mb-3" />
                    <h2 className="text-xl font-bold text-green-300 mb-2">El Salto al Poder</h2>
                    <p className="text-gray-300">
                        El primer nivel de <span className="font-bold text-white">Poder</span> es el <span className="font-bold text-yellow-300">Coraje</span> (200). Es la decisión de tomar responsabilidad, de admitir que tienes el poder de cambiar tu vida. Las siguientes actividades están diseñadas para guiarte en ese salto.
                    </p>
                </div>
                {nextActivity && (
                    <button 
                        onClick={onProceed} 
                        className="inline-flex items-center gap-2 bg-yellow-500 text-black font-bold py-3 px-8 rounded-lg hover:bg-yellow-400 transition-colors"
                    >
                        Entendido, ir a la Siguiente Actividad
                        <ArrowRightIcon className="w-5 h-5"/>
                    </button>
                )}
                <div className="text-center mt-6">
                    <Link to="/cursos/manifiesta" className="text-sm text-gray-500 hover:text-yellow-400 transition-colors">Volver al Temario</Link>
                </div>
            </div>
        </div>
    );
};

export default NivelesConcienciaActivity;